import {
  integrationHomepassPayloadSchema,
  IntegrationHomepassPayloadSchema,
  useIntegrationHomepass,
  useIntegrationHomepassCreate,
  useManagementAreas,
  useMemoTypes,
} from "@/api/generate-hpdb";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FieldSelect } from "@/components/form/field-select";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export function CreateHomepassForm({
  isReadOnly = false,
  onFormDataChange,
}: {
  isReadOnly: boolean;
  onFormDataChange: (hasData: boolean) => void;
}) {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const [managementAreaSearch, setManagementAreaSearch] = useState("");

  const { data: integrationHomepasss } = useIntegrationHomepass({ rfsId: id });
  const { data: managementAreas } = useManagementAreas({
    managementAreaId: managementAreaSearch,
  });
  const { data: memoTypes } = useMemoTypes();

  const integrationHomepass = integrationHomepasss?.[0];

  const memoTypesOptions =
    memoTypes?.data?.map((memoType) => ({
      value: memoType.memoTypeDesc.toString(),
      label: memoType.memoTypeDesc.toString(),
    })) || [];

  const form = useForm<IntegrationHomepassPayloadSchema>({
    resolver: zodResolver(integrationHomepassPayloadSchema),
    values: {
      rfs: {
        id: id,
      },
      managementArea: integrationHomepass?.managementArea ?? "",
      memoTypeDesc: integrationHomepass?.memoTypeDesc ?? "",
    },
  });

  const currentManagementArea =
    form.getValues("managementArea") || integrationHomepass?.managementArea;
  const managementAreasWithoutCurrent = currentManagementArea
    ? managementAreas?.data?.filter(
        (managementArea) =>
          managementArea.mgt?.toString() !== currentManagementArea?.toString(),
      )
    : managementAreas?.data;
  const requiredOptions = currentManagementArea
    ? [
        {
          value: currentManagementArea.toString(),
          label: currentManagementArea.toString(),
        },
      ]
    : [];

  useEffect(() => {
    const hasData = !!integrationHomepass;
    onFormDataChange(hasData);
  }, [integrationHomepass, onFormDataChange]);

  const { mutate, isPending } = useIntegrationHomepassCreate();

  const handleSubmit = (values: IntegrationHomepassPayloadSchema) => {
    mutate(
      {
        data: {
          rfs: {
            id: id,
          },
          managementArea: values.managementArea,
          memoTypeDesc: values.memoTypeDesc,
        },
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Successfully create Homepass",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <div className="space-y-4 md:columns-2">
        <FieldCombobox
          name="managementArea"
          label="Management Area"
          search={managementAreaSearch}
          setSearch={setManagementAreaSearch}
          options={requiredOptions.concat(
            managementAreasWithoutCurrent?.map((managementArea) => ({
              value: managementArea.mgt.toString(),
              label: managementArea.mgt.toString(),
            })) ?? [],
          )}
          isDisabled={isReadOnly || Boolean(integrationHomepass)}
          hasRequiredAsterisk
        />
        <FieldSelect
          name="memoTypeDesc"
          label="Memo Type"
          options={memoTypesOptions}
          isDisabled={isReadOnly || Boolean(integrationHomepass)}
          hasRequiredAsterisk
        />
      </div>
      <SubmitButton
        loading={isPending}
        disabled={isReadOnly || Boolean(integrationHomepass)}
        className="ml-auto"
      />
    </FormContainer>
  );
}
